import {TemplateVariable} from '../../../generated/graphql';
import {isSingleVarString} from '../DefaultResourceConfig/defaultResourceConfigUtil';
import {JsonObject, Property} from '../utilTypes';

export const substituteVariables = (
  config: JsonObject,
  variables: Record<string, number | string>
): JsonObject => {
  function injectVarsIntoString(str: string): string | number {
    let singleValue: string | number | null = null;
    const replaceString = str.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
      singleValue = variables[varName];
      return variables[varName]?.toString() ?? match;
    });
    if (isSingleVarString(str) && singleValue != null) {
      return singleValue;
    }
    return replaceString;
  }

  function substitute(obj: any): any {
    if (typeof obj === 'string') {
      return injectVarsIntoString(obj);
    } else if (typeof obj === 'number') {
      return obj;
    } else if (Array.isArray(obj)) {
      return obj.map(item => substitute(item));
    } else if (typeof obj === 'object' && obj !== null) {
      return Object.fromEntries(
        Object.entries(obj).map(([key, val]) => [key, substitute(val)])
      );
    }
    return obj;
  }

  return substitute(config);
};

export const getDefaultVariableValuesFromQueueVariables = (
  variables: TemplateVariable[]
) => {
  const z = variables.reduce((acc, variable) => {
    const parsedSchema = JSON.parse(variable.schema);
    return {
      ...acc,
      ...(parsedSchema.default != null && {
        [variable.name]: parsedSchema.default,
      }),
    };
  }, {});
  return z;
};

export const getRelevantVariables = (
  serviceConfig: JsonObject,
  allVariables: TemplateVariable[]
) => {
  const configString = JSON.stringify(serviceConfig);
  const variableMatches = configString.match(/\{\{(\w+)\}\}/g) || [];
  const usedVariableNames = new Set(
    variableMatches.map(match => match.replace(/[{}]/g, ''))
  );

  return allVariables.filter(variable => usedVariableNames.has(variable.name));
};

/**
 * Validates that all required services from the job definition are available in the queue config.
 *
 * Args:
 *     jobServices: Dictionary mapping service names to "always" or "never"
 *     queueConfig: The full configuration object from the selected queue
 *
 * Returns:
 *     bool: True if all "always" services are available in the queue config
 *
 * Examples:
 *     validateRequiredServices({"gpu": "always", "storage": "never"}, {"gpu": {...}, "cpu": {...}})
 *     // Returns true because "gpu" (marked as "always") is available
 *
 *     validateRequiredServices({"gpu": "always", "tpu": "always"}, {"gpu": {...}})
 *     // Returns false because "tpu" (marked as "always") is missing
 */
export const validateRequiredServices = (
  jobServices: Record<string, string>,
  queueConfig: Record<string, any> | undefined
): boolean => {
  if (!queueConfig) {
    return Object.keys(jobServices).length === 0;
  }

  const requiredServices = Object.entries(jobServices)
    .filter(([_, requirement]) => requirement === 'always')
    .map(([serviceName, _]) => serviceName);

  return requiredServices.every(serviceName => serviceName in queueConfig);
};

/**
 * Gets the list of missing required services for display in error messages.
 *
 * Args:
 *     jobServices: Dictionary mapping service names to "always" or "never"
 *     queueConfig: The full configuration object from the selected queue
 *
 * Returns:
 *     string[]: Array of service names that are marked as "always" but missing from queue config
 *
 * Examples:
 *     getMissingRequiredServices({"gpu": "always", "tpu": "always"}, {"gpu": {...}})
 *     // Returns ["tpu"]
 */
export const getMissingRequiredServices = (
  jobServices: Record<string, string>,
  queueConfig: Record<string, any> | undefined
): string[] => {
  if (!queueConfig) {
    return Object.entries(jobServices)
      .filter(([_, requirement]) => requirement === 'always')
      .map(([serviceName, _]) => serviceName);
  }

  return Object.entries(jobServices)
    .filter(
      ([serviceName, requirement]) =>
        requirement === 'always' && !(serviceName in queueConfig)
    )
    .map(([serviceName, _]) => serviceName);
};

export const DOT = new RegExp(/(?<!\\)\./g);

export const getNestedOverrideValue = (
  overrides: JsonObject,
  key: string
): any => {
  let root: any = overrides;
  const remainingKeys = key.split(DOT);
  while (remainingKeys.length > 1) {
    const idx = remainingKeys.shift() as string;
    if (root === null || root[idx] == null) {
      return null;
    }
    root = root[idx];
  }
  return root[remainingKeys[0].replaceAll('\\.', '.')];
};

export const hasValue = (value: any): boolean => {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return false;
  }
  return true;
};

export const propertyMapToList = (
  props: any,
  parentKey: string | null,
  file: string | null
): Property[] => {
  const allProperties: Property[] = [];
  for (const key of Object.keys(props)) {
    const p: Property = {
      key: parentKey
        ? parentKey + '.' + key.replaceAll('.', '\\.')
        : key.replaceAll('.', '\\.'),
      file,
      ...props[key],
    };
    allProperties.push(p);
  }
  return allProperties;
};

export const getProperties = (schemas: JsonObject) => {
  const allProperties: Property[] = [];
  const configProps =
    ((schemas['@wandb.config'] as JsonObject)?.properties as any) ?? {};
  allProperties.push(...propertyMapToList(configProps, null, null));
  for (const [file, schema] of Object.entries(schemas.files ?? {})) {
    const props = schema.properties ?? {};
    allProperties.push(...propertyMapToList(props, null, file));
  }
  return allProperties;
};

export const validateRequiredProperties = (
  schemas: JsonObject | null | undefined,
  structuredOverrides: JsonObject,
  structuredFileOverrides: JsonObject
): boolean => {
  if (!schemas) {
    return true;
  }

  const props = getProperties(schemas);
  const flattenedProps = props.reduce((acc: Property[], prop: Property) => {
    if (prop.type === 'object') {
      const nestedProps = propertyMapToList(
        prop.properties as JsonObject,
        prop.key,
        prop.file
      );
      acc.push(...nestedProps);
    } else {
      acc.push(prop);
    }
    return acc;
  }, []);
  const requiredProps = flattenedProps.filter(
    (prop: Property) => prop.required
  );

  for (const prop of requiredProps) {
    let value: any;
    if (prop.file && structuredFileOverrides[prop.file]) {
      value = getNestedOverrideValue(
        structuredFileOverrides[prop.file] as JsonObject,
        prop.key
      );
    } else {
      value = getNestedOverrideValue(structuredOverrides, prop.key);
    }

    if (!hasValue(value)) {
      return false;
    }

    if (prop.type === 'string') {
      if (typeof value !== 'string') {
        return false;
      }

      if (value.trim() === '') {
        return false;
      }
    }

    if (prop.type === 'integer') {
      if (typeof value !== 'number') {
        return false;
      }

      if (!Number.isInteger(Number(value))) {
        return false;
      }

      if (prop.minimum != null && Number(value) < prop.minimum) {
        return false;
      }

      if (prop.maximum != null && Number(value) > prop.maximum) {
        return false;
      }

      if (
        prop.exclusiveMinimum != null &&
        Number(value) <= prop.exclusiveMinimum
      ) {
        return false;
      }

      if (
        prop.exclusiveMaximum != null &&
        Number(value) >= prop.exclusiveMaximum
      ) {
        return false;
      }
    }

    if (prop.type === 'array') {
      if (!Array.isArray(value)) {
        return false;
      }

      if (prop.minItems != null && value.length < prop.minItems) {
        return false;
      }

      if (prop.maxItems != null && value.length > prop.maxItems) {
        return false;
      }
    }
  }

  return true;
};
